import { Link, useLocation, useNavigate, matchPath } from "react-router-dom";
import {
  ApiUrls,
  AppConstants,
  AppDetails,
  SessionStorageKeys,
  UserCookie,
} from "../../utils/constants";
import {
  apiReqResLoader,
  checkEmptyVal,
  GetCookieValues,
  getInitials,
  getPagesPathByLoggedinUserProfile,
  GetUserCookieValues,
  UrlWithoutParam,
} from "../../utils/common";
import routes, { routeNames } from "../../routes/routes";
import { useAuth } from "../../contexts/AuthContext";
import { axiosPost } from "../../helpers/axiosHelper";
import { getPortalClientId, getPortalClientUrl } from "../../helpers/clientHelper";
import config from "../../config.json";
import { deletesessionStorageItem } from "../../helpers/sessionStorageHelper";
import { clearPendingDeepLinkUrl } from "../../utils/deepLinkHelper";

const UserProfileMenu = (styleprops) => {
  const { logoutUser, loggedinUser } = useAuth();
  const navigate = useNavigate();
  const path = useLocation();

  // =======================================================
  //   CONSOLIDATED COOKIE VALUES (SINGLE SOURCE OF TRUTH)
  // =======================================================
  const user = {
    id: GetUserCookieValues(UserCookie.AccountId, loggedinUser),
    name: GetUserCookieValues(UserCookie.Name, loggedinUser),
    pic: GetUserCookieValues(UserCookie.ProfilePic, loggedinUser),

    profileTypeId: GetCookieValues(UserCookie.ProfileTypeId),
    profileType: GetCookieValues(UserCookie.ProfileType),
    accountType: GetCookieValues(UserCookie.AccountType),
    countryId: GetCookieValues(UserCookie.CountryId),
    plan: GetCookieValues(UserCookie.Plan),
    isSetupCompleted: GetCookieValues(UserCookie.IsSetupCompleted),
    sessionId: GetUserCookieValues(UserCookie.SessionId, loggedinUser),
  };

  const loggedinProfileTypeId = user.profileTypeId;
  const userCookieName = user.name;
  const userCookieProfiePic = user.pic;

  // =======================================================
  //   LOGOUT
  // =======================================================
  const onLogout = (e) => {
    e.preventDefault();

    const clientId = getPortalClientId();
    const clientPortalUrl = getPortalClientUrl(loggedinUser);

    // UI logout first
    clearPendingDeepLinkUrl();
    deletesessionStorageItem(SessionStorageKeys.CreateProfileId);
    //set timeout to allow session storage cleanup before logout.
    setTimeout(() => {
      logoutUser();
    }, 1);
    //apiReqResLoader("x");

    if (clientId > 0 && clientPortalUrl) {
      window.location.href = clientPortalUrl;
      return;
    }

    const routesList = routes.filter((r) => r.isprotected);
    const isProtectedRoute = routesList.find(
      (
        rl, // rl.path.toLowerCase() === path.pathname.toLowerCase()
      ) => matchPath({ path: rl.path, end: true }, path.pathname),
    );
    const isPublicDeepLinkRoute = [
      routeNames.publicdocfileviewer.path,
      routeNames.publicdocfolderviewer.path,
      routeNames.publicagreementfileviewer?.path,
    ]
      .filter(Boolean)
      .some((routePath) =>
        matchPath({ path: routePath, end: true }, path.pathname),
      );

    if (isProtectedRoute || isPublicDeepLinkRoute) {
      navigate(routeNames.home.path, { replace: true });
    } else {
      window.location.reload();
    }

    const objBodyParams = {
      accountid: user.id,
      devicetypeid: AppDetails.devicetypeid,
      sessionid: user.sessionId,
    };

    axiosPost(`${config.apiBaseUrl}${ApiUrls.logout}`, objBodyParams)
      .catch((err) => {
        console.error(`"API :: ${ApiUrls.logout}, Error ::" ${err}`);
      })
      .finally(() => {});
  };

  // =======================================================
  //   NAVIGATION HANDLERS
  // =======================================================
  const onUpgradePlan = () => navigate(routeNames.upgradeplan.path);
  const onChangeProfile = () => navigate(routeNames.profiles.path);

  const onNotifications = () =>
    navigate(
      getPagesPathByLoggedinUserProfile(loggedinProfileTypeId, "notifications"),
    );

  const onReports = () => navigate(routeNames.reportsettings.path);
  const onManagePlans = () => navigate(routeNames.upgradeplan.path);

  const onProfile = () => {
    navigate(
      user.isSetupCompleted == 0
        ? UrlWithoutParam(routeNames.createprofile)
        : getPagesPathByLoggedinUserProfile(loggedinProfileTypeId, "profile"),
    );
  };

  return (
    <>
      <li
        className={`nav-item user-info-nav-item ${
          styleprops?.style?.className === "publiclayout"
            ? " position-relative"
            : "pl-5"
        } ${styleprops?.style?.className}`}
      >
        <div
          className="nav-link user-option no-after-cnt pt-0"
          data-bs-toggle="collapse"
          data-bs-target="#collpase-widget-userinfo-menu"
          aria-controls="collpase-widget-userinfo-menu"
          aria-expanded="false"
        >
          <div className="d-flex lh-1">
            {/* =======================================================
                PROFILE PIC OR INITIALS
            ======================================================= */}
            {userCookieProfiePic
              ?.toString()
              .toLowerCase()
              .indexOf("content/images/no-profile-pic.png") == -1 ? (
              <img
                src={userCookieProfiePic}
                alt=""
                className="shadow img-border-white rounded-circle"
              />
            ) : (
              !checkEmptyVal(userCookieName) && (
                <div className="initials-pic flex-shrink-0 rounded-circle w-34px h-34px shadow bo-2-lihover lh-1 font-general font-500 mx-1">
                  {getInitials(userCookieName)}
                </div>
              )
            )}

            {/* =======================================================
                TOP NAV USER NAME + PROFILE DETAILS
            ======================================================= */}
            {user.countryId != 0 ? (
              <div className="pt-1 user-info-name">
                <div>{user.name}</div>
                <div className="mt-1 small text-light">
                  {!checkEmptyVal(user.profileType)
                    ? `${user.profileType} - `
                    : ""}
                  {user.accountType}
                </div>
              </div>
            ) : (
              <>
                {checkEmptyVal(user.profileType) &&
                checkEmptyVal(user.accountType) ? (
                  <div className="user-info-name d-flex flex-center font-general font-500">
                    <div>{user.name}</div>
                  </div>
                ) : (
                  <div className="pt-1 user-info-name">
                    <div>{user.name}</div>
                    <div className="mt-1 small text-light">
                      {!checkEmptyVal(user.profileType)
                        ? `${user.profileType} - `
                        : ""}
                      {user.accountType}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* =======================================================
            DROPDOWN MENU
        ======================================================= */}
        <ul
          className={`dropdown-menu-arrow collapse in user-info-ddmenu ${
            styleprops?.style?.className === "condition" && user.countryId == 0
              ? "condition"
              : ""
          } bg-white position-absolute${
            styleprops?.style?.className === "publiclayout"
              ? " publiclayout"
              : ""
          } py-0 px-0 lh-1 shadow rounded`}
          id="collpase-widget-userinfo-menu"
          style={{ zIndex: styleprops?.style?.zIndex }}
        >
          <div className="row user-info-head py-10 px-10 mx-0 mb-10 box-shadow">
            <div className="container">
              <div className="row px-0">
                <div className="col px-0 wpx-50">
                  {user.pic
                    ?.toLowerCase()
                    .indexOf("content/images/no-profile-pic.png") == -1 ? (
                    <img
                      src={user.pic}
                      alt=""
                      className="user-info-img shadow img-round img-border-white h-40px"
                    />
                  ) : (
                    <div className="initials-pic flex-shrink-0 rounded-circle w-40px h-40px shadow bo-2-lihover lh-1 font-16 font-500 mr-10">
                      {getInitials(user.name)}
                    </div>
                  )}
                </div>

                <div
                  className={`${
                    styleprops?.style?.className === "condition" ||
                    user.countryId == 0
                      ? "d-flex align-items-center"
                      : ""
                  } col px-0 text-left`}
                >
                  <div className="font-500 font-15 lh-20 text-primary">
                    {user.name}
                  </div>

                  {styleprops?.style?.className !== "condition" &&
                    user.countryId != 0 && (
                      <div className="small text-light lh-1">
                        {user.profileType} - {user.accountType}
                      </div>
                    )}
                </div>
              </div>

              {styleprops?.style?.className !== "condition" &&
                user.countryId != 0 && (
                  <div className="row px-0">
                    <div className="col px-0 wpx-50"></div>

                    <div className="col px-0 text-left flex-vc">
                      <div className="small text-primary lh-20 font-small">
                        {`${user.plan} Plan`}
                      </div>
                    </div>

                    <div className="col text-right px-0 w-120 flex-end">
                      <button
                        className="btn btn-primary btn-xs btn-round box-shadow btn-glow"
                        onClick={onUpgradePlan}
                      >
                        Upgrade
                      </button>
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* =======================================================
              MENU ITEMS
          ======================================================= */}
          {styleprops?.style?.className !== "condition" &&
          user.isSetupCompleted != 0 ? (
            <>
              <li className="dropdown-item">
                <a id="nav-user-info-dashboard" onClick={onProfile}>
                  <i className="fa fa-user pe-1"></i> My Profile
                </a>
              </li>
              <li className="dropdown-item">
                <Link id="nav-user-info-settings" to={routeNames.settings.path}>
                  <i className="fa fa-gear pe-1"></i> Settings
                </Link>
              </li>
              <li className="dropdown-item">
                <a id="nav-user-info-profiles" onClick={onChangeProfile}>
                  <i className="fa fa-exchange pe-1"></i> Manage Profiles
                </a>
              </li>
              <li className="dropdown-item">
                <a id="nav-user-info-profiles" onClick={onReports}>
                  <i className="fa fa-pie-chart pe-1"></i> Reports
                </a>
              </li>
              <li className="dropdown-item">
                <a id="nav-user-info-profiles" onClick={onManagePlans}>
                  <i className="fa fa-gem pe-1"></i> Manage Plans
                </a>
              </li>
              <li className="dropdown-item">
                <a id="nav-user-info-profiles" onClick={onNotifications}>
                  <i className="fa fa-bell pe-2"></i> Notifications
                </a>
              </li>
              <li className="dropdown-item d-none">
                <a href="#!">
                  <i className="fa fa-envelope pe-1"></i> Messages
                </a>
              </li>
            </>
          ) : (
            <li className="dropdown-item">
              <a id="nav-user-info-dashboard" onClick={onProfile}>
                <i className="fa fa-user pe-1"></i> My Profile
              </a>
            </li>
          )}

          <div className="dropdown-divider" />

          <li className="dropdown-item">
            <a onClick={onLogout}>
              <i className="fa fa-sign-out pe-1" aria-hidden="true"></i> Sign
              out
            </a>
          </li>
        </ul>
      </li>
    </>
  );
};

export default UserProfileMenu;
